import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import ListFilterToolbar from 'src/common/components/list-filters/ListFilterToolbar';
import UserAutocomplete from 'src/common/components/UserAutocomplete';
import {
  AGE,
  CITY,
  CREATED_AT,
  EMAIL,
  NAME,
  PHONE_NUMBER,
} from 'src/common/constants/participantSortKeys';
import { hasActiveListFilter, hasActiveMultiFilter } from 'src/helpers/listFilters';
import {
  EParticipantFilters,
  type ParticipantFilterValue,
} from 'src/interfaces/Participant';
import ParticipantFilterPopoverContent from 'src/pages/Dashboard/Participants/forms/ParticipantFilterPopover';
import { EOrder } from 'src/interfaces/Order';
import { IUser } from 'src/interfaces/User';
import { EView } from 'src/interfaces/View';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

interface Props {
  checkedInFilter: string[];
  competitionFilter: string[];
  distanceFilter: string[];
  editionFilter: string | null;
  registeredFrom: string | null;
  registeredTo: string | null;
  ownerUserIdFilter?: string | null;
  users?: IUser[];
  onChange: (key: EParticipantFilters, value: ParticipantFilterValue) => void;
  onSetView: (v: EView) => void;
  order: EOrder;
  orderByKey: string;
  query: string;
  setOrder: (a: EOrder) => void;
  setOrderByKey: (a: string) => void;
  view: EView;
  /** Rendered in the toolbar row immediately after the edition selector (e.g. Export). */
  toolbarActions?: ReactNode;
  /** Prefix for `data-tour` attributes (defaults to `"my-participants"`). */
  tourPrefix?: string;
  /** Forwarded to the filter button for guided tour targeting. */
  filterButtonDataTour?: string;
  /** Forwarded to the filter popover paper for guided tour highlighting. */
  filterPopoverDataTour?: string;
  /** When true, render search in the toolbar row instead of the filter popover. */
  searchInToolbar?: boolean;
}

const ParticipantFilterForm = ({
  checkedInFilter,
  competitionFilter,
  distanceFilter,
  editionFilter,
  registeredFrom,
  registeredTo,
  ownerUserIdFilter = null,
  users,
  onChange,
  onSetView,
  order,
  orderByKey,
  query,
  setOrder,
  setOrderByKey,
  view,
  toolbarActions,
  tourPrefix = 'my-participants',
  filterButtonDataTour,
  filterPopoverDataTour,
  searchInToolbar = false,
}: Props) => {
  const { t } = useTranslation();
  const { editions } = useSystemOptionsStore(systemOptionsState => systemOptionsState);

  const sortOptions = [
    { label: t('form.name'), value: NAME },
    { label: t('form.age'), value: AGE },
    { label: t('form.city'), value: CITY },
    { label: t('form.email'), value: EMAIL },
    { label: t('form.phoneNumber'), value: PHONE_NUMBER },
    { label: t('form.byRegistration'), value: CREATED_AT },
  ];

  const showUserFilter = Array.isArray(users);

  const hasActiveFilters =
    hasActiveMultiFilter(checkedInFilter) ||
    hasActiveMultiFilter(competitionFilter) ||
    hasActiveMultiFilter(distanceFilter) ||
    hasActiveListFilter(registeredFrom) ||
    hasActiveListFilter(registeredTo) ||
    (showUserFilter && hasActiveListFilter(ownerUserIdFilter));

  const selectedUser =
    showUserFilter && ownerUserIdFilter
      ? (users.find(u => u.id === ownerUserIdFilter) ?? null)
      : null;

  const editionLeading = (
    <FormControl size="small" sx={{ minWidth: 140, flexShrink: 0 }} data-tour={`${tourPrefix}-edition`}>
      <InputLabel id="edition-selector-label" shrink>
        {t('editions.edition')}
      </InputLabel>
      <Select
        labelId="edition-selector-label"
        value={editionFilter ?? ''}
        label={t('editions.edition')}
        onChange={e => onChange(EParticipantFilters.edition, e.target.value || null)}
        displayEmpty
        renderValue={selected => {
          if (!selected) {
            return t('base.all');
          }
          const selectedEdition = editions?.find(ed => ed.id === selected);
          return selectedEdition ? String(selectedEdition.year) : String(selected);
        }}
      >
        <MenuItem value="">{t('base.all')}</MenuItem>
        {editions?.map(edition => (
          <MenuItem key={edition.id} value={edition.id}>
            {edition.year}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <ListFilterToolbar
      popupId="participantFilters"
      leading={editionLeading}
      toolbarActions={toolbarActions}
      searchQuery={query}
      onSearchChange={q => onChange(EParticipantFilters.query, q)}
      searchPlacement={searchInToolbar ? 'toolbar' : 'popover'}
      hasActiveFilters={hasActiveFilters}
      filterPopoverWidth={480}
      filterPopoverBelowSearch={
        showUserFilter ? (
          <UserAutocomplete
            users={users}
            value={selectedUser}
            onChange={user => onChange(EParticipantFilters.ownerUserId, user?.id ?? null)}
            label={t('users.filterLabel')}
            placeholder={t('users.filterPlaceholder')}
          />
        ) : undefined
      }
      filterPopoverContent={
        <ParticipantFilterPopoverContent
          checkedInFilter={checkedInFilter}
          competitionFilter={competitionFilter}
          distanceFilter={distanceFilter}
          editionFilter={editionFilter}
          registeredFrom={registeredFrom}
          registeredTo={registeredTo}
          onChange={onChange}
        />
      }
      order={order}
      orderByKey={orderByKey}
      setOrder={setOrder}
      setOrderByKey={setOrderByKey}
      sortOptions={sortOptions}
      view={view}
      onSetView={onSetView}
      filterButtonDataTour={filterButtonDataTour}
      filterPopoverDataTour={filterPopoverDataTour}
    />
  );
};

export default ParticipantFilterForm;
